import { BillingSubscriptionDto } from '@shared/core/billing/billing-subscription'
import { ms, requestDeduplicator } from '@shared/util'
import { AxiosError } from 'axios'
import { getReasonPhrase } from 'http-status-codes'
import { useSnackbar } from 'notistack'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { QueryClient, useQueryClient } from 'react-query'
import { TgglContext, useTggl } from 'react-tggl-client'
import { oauthScopes } from 'src/modules/core/constants/oauthScopes'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { LoadingWrapperContext } from 'src/modules/core/context'
import { evaluateRouteAccess } from 'src/modules/core/utils/router.utils'
import { GET_SUBSCRIPTION_CACHE_KEY } from 'src/modules/payment/hooks/use-subscription'
import { AuthContext } from 'src/modules/user/context'
import { useAuth } from 'src/modules/user/hooks/use-auth'
import {
  getPendingConnectedAppAuthorize,
  getPendingConnectedAppAuthorizePath,
} from 'src/modules/user/requirements/pending-connected-app.storage'
import { ApiService } from 'src/services/api.service'
import { BookingOnboardingService } from 'src/services/booking-onboarding.service'
import { TokenService } from 'src/services/token.service'

import { checkScopes, getOAuthErrorCode } from './login.domain'

async function fetchProfileAndSubscription(
  queryClient: QueryClient,
  updateContext: (context: Partial<TgglContext>) => void,
): Promise<{
  profile: Awaited<ReturnType<typeof ApiService.Profile.getProfile>>
  subscription: BillingSubscriptionDto | null
}> {
  const tokenService = TokenService.getInstance()
  if (!tokenService.getToken()) {
    return { profile: null, subscription: null }
  }

  requestDeduplicator.clearAllRequests()
  const profile = await ApiService.Profile.getProfile().catch(() => null)
  let subscription: BillingSubscriptionDto | null = null

  if (profile) {
    updateContext({ userId: profile.id, email: profile.email })
    if (profile.billing_subscription_id) {
      subscription = await queryClient
        .fetchQuery(GET_SUBSCRIPTION_CACHE_KEY, () => ApiService.Billing.getSubscription(), { staleTime: ms('5m') })
        .catch(() => null)
    }
  }

  return { profile, subscription }
}

export function useLogin() {
  const auth = useAuth()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const { formatMessage } = useIntl()
  const { updateContext } = useTggl()
  const { authenticateUser, logoutUser, isReauthorizingForOfflineSync } = useContext(AuthContext)
  const { enableLoading, disableLoading, setLoadingMessage } = useContext(LoadingWrapperContext)

  const handleLoginError = useCallback(
    (errorCode: number, message?: string) => {
      snackbar.enqueueSnackbar(
        formatMessage(
          { id: 'login.error.withCode' },
          {
            code: errorCode,
            message: message ?? getReasonPhrase(errorCode).toLowerCase(),
          },
        ),
        { variant: 'error', autoHideDuration: 9000 },
      )
      disableLoading()
    },
    [disableLoading, formatMessage, snackbar],
  )

  const checkProfileAndRedirect = useCallback(async () => {
    try {
      const { profile } = await fetchProfileAndSubscription(queryClient, updateContext)

      if (!profile) {
        throw new Error('Profile fetch returned null')
      }

      if (window.location.pathname !== routeNames.login.path) {
        return
      }

      const postLoginRedirectPath = sessionStorage.getItem('postLoginRedirectPath')
      if (postLoginRedirectPath) {
        sessionStorage.removeItem('postLoginRedirectPath')
        window.location.href = postLoginRedirectPath
        return
      }

      const redirectToBookingSettings = sessionStorage.getItem('redirectToBookingSettings')
      if (redirectToBookingSettings === 'true') {
        sessionStorage.removeItem('redirectToBookingSettings')
        window.location.href = `${routeNames.settings.path}?tab=booking-page`
        return
      }

      if (profile.is_booking_user) {
        try {
          const bookingOnboarding = await BookingOnboardingService.get()
          if (bookingOnboarding && !bookingOnboarding.onboarding_completed) {
            window.location.href = routeNames.bookingOnboarding.path
            return
          }
        } catch (error) {
          console.error('Failed to fetch booking onboarding:', error)
        }
      }

      const routeEvaluation = evaluateRouteAccess({
        path: routeNames.dashboard.path,
        isAuthenticated: true,
        restricted: true,
        profile,
        isLoading: false,
      })

      if (routeEvaluation.redirect) {
        window.location.href = routeEvaluation.redirectPath
        return
      }

      const pendingConnectedAppPath = getPendingConnectedAppAuthorizePath()
      if (pendingConnectedAppPath) {
        window.location.href = pendingConnectedAppPath
        return
      }

      window.location.href = routeNames.dashboard.path
    } catch (error) {
      logoutUser()
      TokenService.getInstance().removeToken()
      disableLoading()
      snackbar.enqueueSnackbar(formatMessage({ id: 'login.error.unknown' }), { variant: 'error' })
      queryClient.removeQueries()
    }
  }, [formatMessage, logoutUser, queryClient, snackbar, updateContext, disableLoading])

  const processAuthCode = useCallback(
    (code: string, state?: string) => {
      enableLoading()
      const extraData = parseOAuthState(state)
      const urlParams = new URLSearchParams(location.search)
      const isBookingUserFromUrl = urlParams.get('isBookingUser') === 'true'
      const sourceFromUrl = urlParams.get('source')?.trim() || undefined
      const { source: rawStateSource, ...restExtra } = extraData
      const sourceFromState =
        typeof rawStateSource === 'string' && rawStateSource.trim() !== '' ? rawStateSource.trim() : undefined
      // `isBookingUser=true` is set by BOTH the booking add-on and connected
      // apps such as Shopify, so it alone cannot identify the channel. The
      // booking add-on now passes an explicit `source=booking-addon`
      // (booking-addon/src/calendar/ui/onbarding.card.ts), which `sourceFromUrl`
      // picks up ahead of this fallback, so the normal path no longer infers
      // anything.
      //
      // The inference is kept only as a safety net for add-on installs still
      // running an older Apps Script deployment, which lags the web app. It is
      // narrowed on two signals a connected-app flow always produces and the
      // booking add-on never does: an OAuth `state`, and the pending
      // sessionStorage entry.
      //
      // That entry has a 10-minute TTL, so a stale abandoned Shopify attempt
      // can still suppress the inferred tag inside that window. It leaves the
      // source unset rather than wrong, which is the direction we want. Once
      // the new add-on deployment is fully rolled out this fallback can go.
      const hasConnectedAppFlow = Boolean(urlParams.get('state')?.trim()) || getPendingConnectedAppAuthorize() !== null
      const resolvedSource =
        sourceFromUrl ?? sourceFromState ?? (isBookingUserFromUrl && !hasConnectedAppFlow ? 'booking-addon' : undefined)
      auth.mutate(
        {
          ...restExtra,
          ...(isBookingUserFromUrl && { isBookingUser: true }),
          ...(resolvedSource ? { source: resolvedSource } : {}),
          code,
        },
        {
          onSuccess: async (response) => {
            TokenService.getInstance().setToken(response.access_token, response.refresh_token)

            const tokenAfterSet = TokenService.getInstance().getToken()
            if (!tokenAfterSet) {
              handleLoginError(500, 'Failed to store authentication token')
              return
            }

            authenticateUser()

            await new Promise((resolve) => setTimeout(resolve, 100))

            checkProfileAndRedirect()
          },
          onError: (error: AxiosError) => {
            const errorCode = error.response ? error.response.status : 404
            handleLoginError(errorCode)
          },
        },
      )
    },
    [auth, authenticateUser, enableLoading, handleLoginError, checkProfileAndRedirect],
  )

  const loginByCode = useCallback(() => {
    enableLoading()
    setLoadingMessage('Authorize with Google')

    try {
      const forceConsent = sessionStorage.getItem('forceConsentOnNextLogin') === 'true'
      if (forceConsent) {
        sessionStorage.removeItem('forceConsentOnNextLogin')
      }

      const codeClient = window.google.accounts.oauth2.initCodeClient({
        client_id: import.meta.env.VITE_CLIENT_ID,
        ux_mode: 'popup',
        scope: oauthScopes,
        ...(forceConsent && { prompt: 'consent' }),
        callback: async (codeResponse) => {
          if (codeResponse?.error) {
            handleLoginError(getOAuthErrorCode(codeResponse.error))
            return
          }

          if (!checkScopes(oauthScopes, codeResponse)) {
            handleLoginError(403, 'Please try again and ensure all requested permissions are approved during sign-in')
            return
          }

          processAuthCode(codeResponse.code)
        },
      })
      setTimeout(() => {
        codeClient.requestCode()
      }, 100)
    } catch (error) {
      handleLoginError(404, 'Failed to initialize Google OAuth')
    }
  }, [processAuthCode, enableLoading, setLoadingMessage, handleLoginError])

  useEffect(() => {
    disableLoading()
  }, [disableLoading])

  const isRunning = useRef(false)
  useEffect(() => {
    if (isRunning.current) return

    const urlParams = new URLSearchParams(location.search)
    const authorizationCode = urlParams.get('code')
    const state = urlParams.get('state')

    if (authorizationCode) {
      isRunning.current = true
      processAuthCode(authorizationCode, state ?? undefined)
    }
  }, [processAuthCode])

  useEffect(() => {
    if (isReauthorizingForOfflineSync) {
      enableLoading()
    }
  }, [isReauthorizingForOfflineSync, enableLoading])

  const shouldShowContent = !isReauthorizingForOfflineSync

  return {
    shouldShowContent,
    loginByCode,
  }
}

function parseOAuthState(state?: string): Record<string, unknown> {
  if (!state) {
    return {}
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(state))
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>
    }
  } catch {
    // Fallback to plain state passthrough for external app authorize flows.
  }

  return { state }
}
